import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Lock, LogOut, DollarSign, Eye, MousePointerClick, Users, Gauge, TrendingUp, RefreshCw } from "lucide-react";
import { AD_SLOT_CATALOG } from "../lib/adSlots";

const TOKEN_KEY = "stt.adminToken";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(async (t) => {
    setBusy(true);
    try {
      const r = await api.get("/admin/stats", { headers: { Authorization: `Bearer ${t}` } });
      setStats(r.data);
      setErr(null);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setErr("Session expired. Please log in again.");
      } else {
        setErr(e?.response?.data?.detail || "Failed to load stats");
      }
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchStats(token);
  }, [token, fetchStats]);

  const login = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const r = await api.post("/admin/login", { password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      setToken(r.data.token);
      setPassword("");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setStats(null);
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-4 sm:px-5 py-16">
        <div className="glass-strong rounded-2xl p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[rgba(61,107,255,0.15)] border border-[rgba(61,107,255,0.3)]">
              <Lock className="w-5 h-5 text-[var(--cobalt-bright)]" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold">Admin login</h1>
              <p className="text-xs text-[var(--text-muted)] font-mono mt-0.5">Traffic &amp; revenue dashboard</p>
            </div>
          </div>
          <form onSubmit={login} className="space-y-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-[var(--text-muted)] mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-[rgba(15,17,32,0.7)] border border-white/10 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--cobalt)] transition font-mono"
                data-testid="admin-password-input"
                autoFocus
              />
            </div>
            {err && <div className="text-sm text-rose-300" data-testid="admin-login-error">{err}</div>}
            <button
              type="submit"
              disabled={busy || !password}
              className="btn-cobalt w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium disabled:opacity-60"
              data-testid="admin-login-btn"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 py-8 sm:py-12">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--cobalt-bright)] mb-2">/ Admin</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Traffic &amp; revenue</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => fetchStats(token)} disabled={busy} className="btn-ghost inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium" data-testid="admin-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button onClick={logout} className="btn-ghost inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium" data-testid="admin-logout-btn">
            <LogOut className="w-3.5 h-3.5" /> Log out
          </button>
        </div>
      </header>

      {err && <div className="glass rounded-xl p-4 mb-6 border border-rose-500/30 text-rose-200 text-sm">{err}</div>}

      {!stats ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="glass rounded-xl p-5 h-28 animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <Kpi icon={Eye} label="Pageviews · Today" value={stats.pageviews.today} testid="kpi-pv-today" />
            <Kpi icon={Eye} label="Pageviews · Week" value={stats.pageviews.week} testid="kpi-pv-week" />
            <Kpi icon={Eye} label="Pageviews · Month" value={stats.pageviews.month} testid="kpi-pv-month" />
            <Kpi icon={Eye} label="Pageviews · Total" value={stats.pageviews.total} highlight testid="kpi-pv-total" />

            <Kpi icon={Users} label="Uniques · Today" value={stats.unique_visitors.today} testid="kpi-uv-today" />
            <Kpi icon={Users} label="Uniques · Week" value={stats.unique_visitors.week} testid="kpi-uv-week" />
            <Kpi icon={Users} label="Uniques · Month" value={stats.unique_visitors.month} testid="kpi-uv-month" />
            <Kpi icon={Gauge} label="Speed tests run" value={stats.speedtests_total} testid="kpi-speedtests" />

            <Kpi icon={MousePointerClick} label="Clicks · Today" value={stats.button_clicks.today} testid="kpi-clicks-today" />
            <Kpi icon={MousePointerClick} label="Clicks · Week" value={stats.button_clicks.week} testid="kpi-clicks-week" />
            <Kpi icon={MousePointerClick} label="Clicks · Month" value={stats.button_clicks.month} testid="kpi-clicks-month" />
            <Kpi icon={MousePointerClick} label="Clicks · Total" value={stats.button_clicks.total} highlight testid="kpi-clicks-total" />
          </section>

          {/* Revenue */}
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <div className="lg:col-span-2 glass-strong rounded-2xl p-5 sm:p-6 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full" style={{ background: "radial-gradient(closest-side, rgba(52,211,153,0.2), transparent)" }} />
              <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-4 h-4 text-emerald-400" />
                  <span className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-muted)]">Estimated ad revenue</span>
                </div>
                <div className="font-display text-4xl sm:text-5xl font-semibold tabular-nums" data-testid="revenue-total">
                  ${stats.revenue_usd.total.toFixed(2)}
                </div>
                <div className="text-xs text-[var(--text-muted)] font-mono mt-2">
                  ≈ CPM ${stats.cpm_usd.toFixed(2)} · CPC ${stats.cpc_usd.toFixed(2)} · lifetime estimate
                </div>

                <div className="mt-6 grid grid-cols-3 gap-3">
                  <RevMini label="Today" value={stats.revenue_usd.today} testid="revenue-today" />
                  <RevMini label="7 days" value={stats.revenue_usd.week} testid="revenue-week" />
                  <RevMini label="30 days" value={stats.revenue_usd.month} testid="revenue-month" />
                </div>
              </div>
            </div>

            <div className="glass rounded-2xl p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-[var(--cobalt-bright)]" />
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-muted)]">AdSense performance</span>
              </div>
              <div className="space-y-4 text-sm">
                <Row k="Impressions" v={stats.ads.impressions.toLocaleString()} testid="ad-impressions" />
                <Row k="Clicks" v={stats.ads.clicks.toLocaleString()} testid="ad-clicks" />
                <Row k="CTR" v={`${stats.ads.ctr}%`} testid="ad-ctr" />
              </div>
              <p className="text-xs text-[var(--text-muted)] font-mono mt-5 leading-relaxed">
                Impressions &amp; clicks captured from in-viewport ad slots. Revenue below uses CPM ${stats.cpm_usd.toFixed(2)} / CPC ${stats.cpc_usd.toFixed(2)}.
              </p>
            </div>
          </section>

          {/* Per-slot leaderboard */}
          <section className="glass rounded-2xl p-5 sm:p-6 mb-6 overflow-hidden" data-testid="ad-slot-leaderboard">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <h3 className="font-display text-base font-semibold">Slot performance</h3>
              <span className="text-xs text-[var(--text-muted)] font-mono">all configured slots · ranked by revenue</span>
            </div>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-mono">
                    <th className="py-2 px-2">#</th>
                    <th className="py-2 px-2">Slot</th>
                    <th className="py-2 px-2 text-right">Impr.</th>
                    <th className="py-2 px-2 text-right">Clicks</th>
                    <th className="py-2 px-2 text-right">CTR</th>
                    <th className="py-2 px-2 text-right">Rev. est.</th>
                  </tr>
                </thead>
                <tbody>
                  {mergeSlots(stats.ads.slots, stats.cpm_usd, stats.cpc_usd).map((s, i) => (
                    <tr key={s.name} className="border-t border-white/5" data-testid={`ad-slot-row-${s.name}`}>
                      <td className="py-2.5 px-2 font-mono text-[var(--text-muted)]">{i + 1}</td>
                      <td className="py-2.5 px-2">
                        <div className="text-sm">{s.label}</div>
                        <div className="text-[10px] font-mono text-[var(--text-muted)]">
                          <span className="text-[var(--text-dim)]">{s.name}</span>
                          {s.slot_id && <> · ID {s.slot_id}</>}
                        </div>
                      </td>
                      <td className="py-2.5 px-2 text-right tabular-nums">{s.impressions.toLocaleString()}</td>
                      <td className="py-2.5 px-2 text-right tabular-nums">{s.clicks.toLocaleString()}</td>
                      <td className="py-2.5 px-2 text-right tabular-nums">
                        {s.impressions === 0 ? (
                          <span className="text-[var(--text-muted)]">—</span>
                        ) : (
                          <span className={s.ctr >= 2 ? "text-emerald-300" : s.ctr >= 0.5 ? "text-amber-300" : "text-[var(--text-dim)]"}>
                            {s.ctr}%
                          </span>
                        )}
                      </td>
                      <td className={`py-2.5 px-2 text-right tabular-nums font-medium ${s.revenue_usd > 0 ? "text-emerald-300" : "text-[var(--text-muted)]"}`}>
                        ${s.revenue_usd.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] font-mono text-[var(--text-muted)] mt-4 leading-relaxed">
              CTR color: <span className="text-emerald-300">≥2%</span> is great, <span className="text-amber-300">≥0.5%</span> is normal, below that means the slot is under-performing — consider moving it.
            </p>
          </section>

          {/* Chart */}
          <section className="glass rounded-2xl p-4 sm:p-6 mb-6 overflow-hidden" data-testid="chart-pageviews">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-base font-semibold">Pageviews · last 30 days</h3>
              <span className="text-xs text-[var(--text-muted)] font-mono">{stats.timeseries.reduce((s, p) => s + p.pageviews, 0)} total</span>
            </div>
            <div className="w-full h-56 sm:h-64 overflow-hidden">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.timeseries} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="pvGrad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#4A78FF" />
                      <stop offset="100%" stopColor="#5C85FF" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(138,160,255,0.08)" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fill: "#5A6487", fontSize: 10 }} tickFormatter={(v) => v.slice(5)} minTickGap={20} />
                  <YAxis tick={{ fill: "#5A6487", fontSize: 10 }} allowDecimals={false} width={30} />
                  <Tooltip contentStyle={{ background: "#0F1120", border: "1px solid rgba(138,160,255,0.2)", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#8892B8" }} />
                  <Line type="monotone" dataKey="pageviews" stroke="url(#pvGrad)" strokeWidth={2.5} dot={false} activeDot={{ r: 4, fill: "#5C85FF" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* Top paths + buttons */}
          <section className="grid md:grid-cols-2 gap-4">
            <div className="glass rounded-2xl p-5 sm:p-6" data-testid="top-paths">
              <h3 className="font-display text-base font-semibold mb-4">Top pages</h3>
              {stats.top_paths.length === 0 ? (
                <div className="text-sm text-[var(--text-muted)]">No data yet.</div>
              ) : (
                <ul className="space-y-3">
                  {stats.top_paths.map((p, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <span className="font-mono text-xs text-[var(--text-muted)] w-6">{i + 1}.</span>
                      <span className="flex-1 font-mono truncate">{p.path}</span>
                      <span className="tabular-nums text-[var(--cobalt-bright)]">{p.views}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="glass rounded-2xl p-5 sm:p-6" data-testid="top-buttons">
              <h3 className="font-display text-base font-semibold mb-4">Top buttons</h3>
              {stats.top_buttons.length === 0 ? (
                <div className="text-sm text-[var(--text-muted)]">No clicks tracked yet.</div>
              ) : (
                <ul className="space-y-3">
                  {stats.top_buttons.map((b, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <span className="font-mono text-xs text-[var(--text-muted)] w-6">{i + 1}.</span>
                      <span className="flex-1 font-mono truncate">{b.name}</span>
                      <span className="tabular-nums text-[var(--cobalt-bright)]">{b.clicks}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, highlight, testid }) {
  return (
    <div className={`glass rounded-xl p-4 sm:p-5 ${highlight ? "border-[rgba(61,107,255,0.35)] bg-[rgba(61,107,255,0.06)]" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[var(--text-muted)] mb-2">
        <Icon className="w-3.5 h-3.5" />
        <span className="truncate">{label}</span>
      </div>
      <div className="font-display text-2xl sm:text-3xl font-semibold tabular-nums">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function RevMini({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs font-mono uppercase tracking-wider text-[var(--text-muted)]">{label}</div>
      <div className="font-display text-lg sm:text-xl font-semibold tabular-nums text-emerald-300 mt-1">
        ${Number(value).toFixed(2)}
      </div>
    </div>
  );
}

function Row({ k, v, testid }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className="text-[var(--text-dim)]">{k}</span>
      <span className="font-mono tabular-nums">{v}</span>
    </div>
  );
}

/**
 * Merge configured slot catalog with server-side live stats.
 * - Every configured slot appears even with 0 impressions.
 * - Filters out TEST_* names (seed data from testing agents).
 * - Sorts by revenue desc, then impressions desc, then label.
 */
function mergeSlots(serverSlots, cpm, cpc) {
  const byName = {};
  for (const s of serverSlots || []) {
    if (!s.name || s.name.startsWith("TEST_")) continue;
    byName[s.name] = s;
  }
  const merged = AD_SLOT_CATALOG.map((cfg) => {
    const live = byName[cfg.name];
    if (live) {
      delete byName[cfg.name];
      return {
        name: cfg.name,
        label: cfg.label,
        slot_id: live.slot_id || cfg.slotId,
        impressions: live.impressions || 0,
        clicks: live.clicks || 0,
        ctr: live.ctr || 0,
        revenue_usd: live.revenue_usd || 0,
      };
    }
    return {
      name: cfg.name,
      label: cfg.label,
      slot_id: cfg.slotId,
      impressions: 0,
      clicks: 0,
      ctr: 0,
      revenue_usd: 0,
    };
  });
  // Append any unknown slots (future-proof)
  for (const name of Object.keys(byName)) {
    const s = byName[name];
    merged.push({
      name,
      label: name,
      slot_id: s.slot_id,
      impressions: s.impressions || 0,
      clicks: s.clicks || 0,
      ctr: s.ctr || 0,
      revenue_usd: s.revenue_usd || 0,
    });
  }
  merged.sort((a, b) => b.revenue_usd - a.revenue_usd || b.impressions - a.impressions || a.label.localeCompare(b.label));
  return merged;
}
